/**
 * Service pour récupérer les données du tableau de bord
 */
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  orderBy,
  limit as limitQuery,
  getCountFromServer,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { vehicleService } from './vehicleService';
import { userService } from './userService';
import { fleetService } from './fleetService';
import { fuelConsumptionService } from './fuelConsumptionService';

// Types pour les données du tableau de bord
export interface GeneralStats {
  totalVehicles: number;
  activeMembers: number;
  totalKilometers: number;
}

export interface FleetDistributionItem {
  name: string;
  value: number;
  color: string;
}

export interface MemberDistribution {
  activeMembers: number;
  vehiclesPerMember: number;
  regularDrivers: number;
  occasionalUsers: number;
}

export interface MileageStats {
  totalKilometers: number;
  averagePerVehicle: number;
  monthlyReports: number;
  monthlyChange: number; // pourcentage de changement par rapport au mois précédent
}

export interface MaintenanceAndFuelStats {
  maintenanceFiles: number;
  plannedMaintenance: number;
  fuelTickets: number;
  totalExpenses: number;
}

export interface RecentActivity {
  id: string;
  type: 'fuel' | 'maintenance' | 'member' | 'vehicle';
  description: string;
  timestamp: Date;
  relatedEntityId?: string;
}

export interface FleetStatus {
  totalVehicles: number;
  availableVehicles: number;
  inMaintenance: number;
  outOfService: number;
}

// Couleurs pour les graphiques
const CHART_COLORS = [
  '#2196f3', // Bleu
  '#f44336', // Rouge
  '#4caf50', // Vert
  '#ff9800', // Orange
  '#9c27b0', // Violet
  '#795548', // Marron
  '#607d8b', // Bleu-gris
  '#e91e63', // Rose
  '#009688', // Turquoise
  '#cddc39', // Vert-jaune
];

/**
 * Service pour récupérer les données du tableau de bord
 * Implémenté avec des requêtes Firestore
 */
export const dashboardService = {
  /**
   * Récupère les statistiques générales
   * @param organizationId ID de l'organisation
   * @returns Statistiques générales
   */
  getGeneralStats: async (organizationId: string): Promise<GeneralStats> => {
    try {
      console.log(`Fetching general stats for organization: ${organizationId}`);
      
      // Récupérer le nombre total de véhicules
      const vehiclesQuery = query(
        collection(db, 'vehicles'),
        where('organizationId', '==', organizationId)
      );
      const vehiclesSnapshot = await getCountFromServer(vehiclesQuery);
      const totalVehicles = vehiclesSnapshot.data().count;
      
      // Récupérer le nombre de membres actifs
      const membersQuery = query(
        collection(db, 'users'),
        where('organizationId', '==', organizationId),
        where('status', '==', 'active')
      );
      const membersSnapshot = await getCountFromServer(membersQuery);
      const activeMembers = membersSnapshot.data().count;
      
      // Récupérer tous les véhicules pour calculer le kilométrage total
      const vehicles = await vehicleService.getVehiclesByOrganization(organizationId);
      const totalKilometers = vehicles.reduce((sum, vehicle) => sum + (vehicle.currentMileage || 0), 0);
      
      return {
        totalVehicles,
        activeMembers,
        totalKilometers
      };
    } catch (error) {
      console.error('Error fetching general stats:', error);
      throw new Error('Failed to fetch general statistics');
    }
  },
  
  /**
   * Récupère la répartition par flotte
   * @param organizationId ID de l'organisation
   * @returns Répartition par flotte
   */
  getFleetDistribution: async (organizationId: string): Promise<FleetDistributionItem[]> => {
    try {
      console.log(`Fetching fleet distribution for organization: ${organizationId}`);
      
      // Récupérer toutes les flottes de l'organisation
      const fleets = await fleetService.getFleets(organizationId);
      
      // Pour chaque flotte, récupérer le nombre de véhicules
      const fleetDistribution: FleetDistributionItem[] = [];
      
      for (let i = 0; i < fleets.length; i++) {
        const fleet = fleets[i];
        const vehicles = await vehicleService.getVehiclesByFleet(fleet.id);
        
        fleetDistribution.push({
          name: fleet.name,
          value: vehicles.length,
          color: CHART_COLORS[i % CHART_COLORS.length]
        });
      }
      
      // Ajouter une catégorie pour les véhicules sans flotte
      const vehiclesQuery = query(
        collection(db, 'vehicles'),
        where('organizationId', '==', organizationId),
        where('fleetId', '==', null)
      );
      const vehiclesSnapshot = await getCountFromServer(vehiclesQuery);
      const unassignedVehicles = vehiclesSnapshot.data().count;
      
      if (unassignedVehicles > 0) {
        fleetDistribution.push({
          name: 'Non assignés',
          value: unassignedVehicles,
          color: CHART_COLORS[fleetDistribution.length % CHART_COLORS.length]
        });
      }
      
      return fleetDistribution;
    } catch (error) {
      console.error('Error fetching fleet distribution:', error);
      throw new Error('Failed to fetch fleet distribution');
    }
  },
  
  /**
   * Récupère la répartition par membre
   * @param organizationId ID de l'organisation
   * @returns Répartition par membre
   */
  getMemberDistribution: async (organizationId: string): Promise<MemberDistribution> => {
    try {
      console.log(`Fetching member distribution for organization: ${organizationId}`);
      
      // Récupérer le nombre de membres actifs
      const membersQuery = query(
        collection(db, 'users'),
        where('organizationId', '==', organizationId),
        where('status', '==', 'active')
      );
      const membersSnapshot = await getCountFromServer(membersQuery);
      const activeMembers = membersSnapshot.data().count;
      
      // Récupérer tous les véhicules pour calculer les statistiques
      const vehicles = await vehicleService.getVehiclesByOrganization(organizationId);
      const totalVehicles = vehicles.length;
      
      // Calculer le nombre de véhicules par membre
      const vehiclesPerMember = activeMembers > 0 ? totalVehicles / activeMembers : 0;
      
      // Compter les conducteurs réguliers (avec un véhicule assigné)
      const assignedVehicles = vehicles.filter(v => v.currentAssignment);
      const regularDrivers = new Set(assignedVehicles.map(v => v.currentAssignment?.userId)).size;
      
      // Les utilisateurs occasionnels sont les membres actifs moins les conducteurs réguliers
      const occasionalUsers = Math.max(0, activeMembers - regularDrivers);
      
      return {
        activeMembers,
        vehiclesPerMember: parseFloat(vehiclesPerMember.toFixed(1)),
        regularDrivers,
        occasionalUsers
      };
    } catch (error) {
      console.error('Error fetching member distribution:', error);
      throw new Error('Failed to fetch member distribution');
    }
  },
  
  /**
   * Récupère les statistiques kilométriques
   * @param organizationId ID de l'organisation
   * @returns Statistiques kilométriques
   */
  getMileageStats: async (organizationId: string): Promise<MileageStats> => {
    try {
      console.log(`Fetching mileage stats for organization: ${organizationId}`);
      
      // Récupérer tous les véhicules pour calculer le kilométrage total et moyen
      const vehicles = await vehicleService.getVehiclesByOrganization(organizationId);
      const totalKilometers = vehicles.reduce((sum, vehicle) => sum + (vehicle.currentMileage || 0), 0);
      const averagePerVehicle = vehicles.length > 0 ? totalKilometers / vehicles.length : 0;
      
      // Calculer le nombre de rapports kilométriques ce mois-ci
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const mileageReportsQuery = query(
        collection(db, 'mileageReports'),
        where('organizationId', '==', organizationId),
        where('date', '>=', Timestamp.fromDate(firstDayOfMonth))
      );
      const mileageReportsSnapshot = await getCountFromServer(mileageReportsQuery);
      const monthlyReports = mileageReportsSnapshot.data().count;
      
      // Calculer le changement par rapport au mois précédent
      const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      const lastMonthReportsQuery = query(
        collection(db, 'mileageReports'),
        where('organizationId', '==', organizationId),
        where('date', '>=', Timestamp.fromDate(firstDayOfLastMonth)),
        where('date', '<=', Timestamp.fromDate(lastDayOfLastMonth))
      );
      const lastMonthReportsSnapshot = await getCountFromServer(lastMonthReportsQuery);
      const lastMonthReports = lastMonthReportsSnapshot.data().count;
      
      // Calculer le pourcentage de changement
      let monthlyChange = 0;
      if (lastMonthReports > 0) {
        monthlyChange = ((monthlyReports - lastMonthReports) / lastMonthReports) * 100;
      } else if (monthlyReports > 0) {
        monthlyChange = 100; // Si pas de rapports le mois dernier mais des rapports ce mois-ci
      }
      
      return {
        totalKilometers,
        averagePerVehicle: Math.round(averagePerVehicle),
        monthlyReports,
        monthlyChange: parseFloat(monthlyChange.toFixed(1))
      };
    } catch (error) {
      console.error('Error fetching mileage stats:', error);
      throw new Error('Failed to fetch mileage statistics');
    }
  },
  
  /**
   * Récupère les statistiques d'entretien et carburant
   * @param organizationId ID de l'organisation
   * @returns Statistiques d'entretien et carburant
   */
  getMaintenanceAndFuelStats: async (organizationId: string): Promise<MaintenanceAndFuelStats> => {
    try {
      console.log(`Fetching maintenance and fuel stats for organization: ${organizationId}`);
      
      // Récupérer le nombre de fiches d'entretien
      const maintenanceQuery = query(
        collection(db, 'maintenanceRecords'),
        where('organizationId', '==', organizationId)
      );
      const maintenanceSnapshot = await getCountFromServer(maintenanceQuery);
      const maintenanceFiles = maintenanceSnapshot.data().count;
      
      // Récupérer le nombre d'entretiens planifiés
      const plannedMaintenanceQuery = query(
        collection(db, 'maintenanceRecords'),
        where('organizationId', '==', organizationId),
        where('status', '==', 'planned')
      );
      const plannedMaintenanceSnapshot = await getCountFromServer(plannedMaintenanceQuery);
      const plannedMaintenance = plannedMaintenanceSnapshot.data().count;
      
      // Récupérer le nombre de tickets carburant
      const fuelConsumptions = await fuelConsumptionService.getByOrganization(organizationId);
      const fuelTickets = fuelConsumptions.length;
      
      // Calculer les dépenses totales (entretien + carburant)
      const maintenanceRecordsQuery = query(
        collection(db, 'maintenanceRecords'),
        where('organizationId', '==', organizationId)
      );
      const maintenanceRecordsSnapshot = await getDocs(maintenanceRecordsQuery);
      const maintenanceExpenses = maintenanceRecordsSnapshot.docs.reduce(
        (sum, doc) => sum + (doc.data().costTTC || 0),
        0
      );
      
      const fuelExpenses = fuelConsumptions.reduce(
        (sum, consumption) => sum + (consumption.amount || 0),
        0
      );
      
      const totalExpenses = maintenanceExpenses + fuelExpenses;
      
      return {
        maintenanceFiles,
        plannedMaintenance,
        fuelTickets,
        totalExpenses: Math.round(totalExpenses)
      };
    } catch (error) {
      console.error('Error fetching maintenance and fuel stats:', error);
      throw new Error('Failed to fetch maintenance and fuel statistics');
    }
  },
  
  /**
   * Récupère les activités récentes
   * @param organizationId ID de l'organisation
   * @param limit Nombre maximum d'activités à récupérer
   * @returns Liste des activités récentes
   */
  getRecentActivities: async (organizationId: string, limit = 5): Promise<RecentActivity[]> => {
    try {
      console.log(`Fetching recent activities for organization: ${organizationId}, limit: ${limit}`);
      
      const activities: RecentActivity[] = [];
      
      // Récupérer les tickets carburant récents
      const fuelQuery = query(
        collection(db, 'fuelConsumptions'),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc'),
        limitQuery(limit)
      );
      const fuelSnapshot = await getDocs(fuelQuery);
      
      for (const doc of fuelSnapshot.docs) {
        const data = doc.data();
        const vehicle = await vehicleService.getVehicle(data.vehicleId);
        
        activities.push({
          id: doc.id,
          type: 'fuel',
          description: `Ticket carburant ajouté - Véhicule ${vehicle?.licensePlate || data.vehicleId}`,
          timestamp: data.createdAt.toDate(),
          relatedEntityId: data.vehicleId
        });
      }
      
      // Récupérer les maintenances récentes
      const maintenanceQuery = query(
        collection(db, 'maintenanceRecords'),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc'),
        limitQuery(limit)
      );
      const maintenanceSnapshot = await getDocs(maintenanceQuery);
      
      for (const doc of maintenanceSnapshot.docs) {
        const data = doc.data();
        const vehicle = await vehicleService.getVehicle(data.vehicleId);
        
        activities.push({
          id: doc.id,
          type: 'maintenance',
          description: `Entretien ${data.status === 'planned' ? 'programmé' : 'enregistré'} - Véhicule ${vehicle?.licensePlate || data.vehicleId}`,
          timestamp: data.createdAt.toDate(),
          relatedEntityId: data.vehicleId
        });
      }
      
      // Récupérer les nouveaux membres récents
      const membersQuery = query(
        collection(db, 'users'),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc'),
        limitQuery(limit)
      );
      const membersSnapshot = await getDocs(membersQuery);
      
      for (const doc of membersSnapshot.docs) {
        const data = doc.data();
        
        activities.push({
          id: doc.id,
          type: 'member',
          description: `Nouveau membre ajouté - ${data.displayName || data.email}`,
          timestamp: data.createdAt.toDate(),
          relatedEntityId: doc.id
        });
      }
      
      // Récupérer les nouveaux véhicules récents
      const vehiclesQuery = query(
        collection(db, 'vehicles'),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc'),
        limitQuery(limit)
      );
      const vehiclesSnapshot = await getDocs(vehiclesQuery);
      
      for (const doc of vehiclesSnapshot.docs) {
        const data = doc.data();
        
        activities.push({
          id: doc.id,
          type: 'vehicle',
          description: `Nouveau véhicule ajouté - ${data.licensePlate || `${data.make} ${data.model}`}`,
          timestamp: data.createdAt.toDate(),
          relatedEntityId: doc.id
        });
      }
      
      // Trier toutes les activités par date et limiter au nombre demandé
      return activities
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      throw new Error('Failed to fetch recent activities');
    }
  },
  
  /**
   * Récupère l'état de la flotte
   * @param organizationId ID de l'organisation
   * @returns État de la flotte
   */
  getFleetStatus: async (organizationId: string): Promise<FleetStatus> => {
    try {
      console.log(`Fetching fleet status for organization: ${organizationId}`);
      
      // Récupérer le nombre total de véhicules
      const totalVehiclesQuery = query(
        collection(db, 'vehicles'),
        where('organizationId', '==', organizationId)
      );
      const totalVehiclesSnapshot = await getCountFromServer(totalVehiclesQuery);
      const totalVehicles = totalVehiclesSnapshot.data().count;
      
      // Récupérer le nombre de véhicules disponibles
      const availableVehiclesQuery = query(
        collection(db, 'vehicles'),
        where('organizationId', '==', organizationId),
        where('status', '==', 'active'),
        where('reservationStatus', '==', 'available')
      );
      const availableVehiclesSnapshot = await getCountFromServer(availableVehiclesQuery);
      let availableVehicles = availableVehiclesSnapshot.data().count;
      
      // Si le champ reservationStatus n'est pas utilisé, compter les véhicules actifs sans maintenance en cours
      if (availableVehicles === 0) {
        const activeVehiclesQuery = query(
          collection(db, 'vehicles'),
          where('organizationId', '==', organizationId),
          where('status', '==', 'active')
        );
        const activeVehiclesSnapshot = await getCountFromServer(activeVehiclesQuery);
        availableVehicles = activeVehiclesSnapshot.data().count;
      }
      
      // Récupérer le nombre de véhicules en maintenance
      const inMaintenanceQuery = query(
        collection(db, 'vehicles'),
        where('organizationId', '==', organizationId),
        where('status', '==', 'maintenance')
      );
      const inMaintenanceSnapshot = await getCountFromServer(inMaintenanceQuery);
      const inMaintenance = inMaintenanceSnapshot.data().count;
      
      // Récupérer le nombre de véhicules hors service
      const outOfServiceQuery = query(
        collection(db, 'vehicles'),
        where('organizationId', '==', organizationId),
        where('status', '==', 'out_of_service')
      );
      const outOfServiceSnapshot = await getCountFromServer(outOfServiceQuery);
      const outOfService = outOfServiceSnapshot.data().count;
      
      return {
        totalVehicles,
        availableVehicles,
        inMaintenance,
        outOfService
      };
    } catch (error) {
      console.error('Error fetching fleet status:', error);
      throw new Error('Failed to fetch fleet status');
    }
  }
};

export default dashboardService;